// ./src/components/guards/OnboardingGuard/OnboardingGuard.tsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { USER_ROLES } from "@/config";
import { useApp } from "@/contexts/AppContext";
import { useSignUpRequiresCompany } from "@/hooks/useSignUpRequiresCompany";
import { accountNameMissing, onboardingGateTarget } from "@/components/features/Onboarding/onboardingSteps";

// ============================================
// Types
// ============================================

export interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Where the brand asks for a company, an owner whose account has no name yet goes to the onboarding's first step before
 * anything else (owner, 2026-11-02: moved off the sign-up form and "make it required there"). It holds across sign-ins: the
 * rule is the account's name, not a flag. Every other account goes straight through, with no read at all.
 */
export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({
  children,
}) => {
  const { user } = useApp();
  const { pathname } = useLocation();
  const isOwner = user?.userRole?.id === USER_ROLES.OWNER;
  const accountName = user?.account?.name;
  const unnamed = isOwner && accountNameMissing(accountName);
  const requiresCompany = useSignUpRequiresCompany(unnamed);
  const target = onboardingGateTarget({ isOwner, accountName, requiresCompany, pathname });

  if (target) {
    return <Navigate to={target} replace />;
  }

  // ⓘ A moment, for an unnamed account only: no dashboard to flash before the answer turns it away.
  if (unnamed && requiresCompany === undefined) {
    return null;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
